import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import {
  EMPTY_CONTACT, CONTACT_SUBJECTS, SUPPORT_EMAIL, MAILBOXES, routeFor,
  validateContact, mailtoHref, type ContactMessage,
} from "../domain/contact";
import { ENTITY, entityIdentifiersLine } from "../domain/entity";
import { VersionLine } from "../components/VersionLine";

/**
 * Contact us.
 *
 * 🚨 THERE IS NO TRANSPORT BEHIND THIS FORM. MSG91 is not configured and no
 * mail sender is wired, so pressing a button cannot deliver anything. The page
 * is built this way on purpose rather than hidden until it works:
 *
 *   - the EMAIL ADDRESS is given first and prominently, because it is the only
 *     path that reaches a human today;
 *   - the form is present so its wording and fields can be approved now;
 *   - it never claims to have sent. It composes a mailto: carrying everything
 *     typed, so the visitor's effort is not thrown away.
 *
 * ⛔ Do not add a "Send" that posts nowhere and shows a success message. A form
 * that says "thank you, we'll be in touch" and drops the message is worse than
 * no form: the visitor stops looking for another way to reach us.
 *
 * ▶️ When a sender exists, replace the mailto button with the POST and keep
 * validateContact — the rules are already the ones a backend would need.
 */
export function Contact() {
  const [msg, setMsg] = useState<ContactMessage>(EMPTY_CONTACT);
  const set = <K extends keyof ContactMessage>(k: K, v: ContactMessage[K]) =>
    setMsg((m) => ({ ...m, [k]: v }));
  const problems = validateContact(msg);
  const ready = problems.length === 0;

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
          <Button size="small" component={RouterLink} to="/pricing">Pricing</Button>
          <Button size="small" component={RouterLink} to="/register">Register</Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Contact us</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Questions about plans, registering as a provider, an invoice, or your data.
        </Typography>

        {/* The addresses come FIRST because they are the thing that works.
            🔑 Listed with what each is FOR. Three bare addresses make the
            visitor guess, and a wrong guess costs them a forward. */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
            <EmailIcon color="primary" sx={{ mt: 0.25 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Email us</Typography>
          </Stack>
          <Stack spacing={1.25} sx={{ pl: { xs: 0, sm: 4.5 } }}>
            {MAILBOXES.map((b) => (
              <Box key={b.address}>
                <Link href={`mailto:${b.address}`} sx={{ fontWeight: 600 }}>{b.address}</Link>
                <Typography variant="body2" color="text.secondary">{b.purpose}</Typography>
              </Box>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, pl: { xs: 0, sm: 4.5 } }}>
            Not sure which? Use <strong>{SUPPORT_EMAIL}</strong> and we will pass it on. Response
            targets for existing customers are in the{" "}
            <Link component={RouterLink} to="/legal/support">Support Policy</Link>.
          </Typography>
        </Paper>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>This form does not send yet</AlertTitle>
          We have not finished wiring a mail sender, and we would rather say so than show you a
          form that quietly discards what you wrote. Fill it in and the button below will open
          your own mail app with everything already composed, addressed to whichever mailbox
          fits your subject — or just write to one of the addresses above directly.
        </Alert>

        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Your name" value={msg.name}
                onChange={(e) => set("name", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Email" value={msg.email}
                helperText="How we reply"
                onChange={(e) => set("email", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Organisation (optional)"
                value={msg.organisation}
                onChange={(e) => set("organisation", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth size="small" label="What is this about?"
                value={msg.subject} onChange={(e) => set("subject", e.target.value)}>
                {CONTACT_SUBJECTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline minRows={5} size="small" label="Message"
                value={msg.message} onChange={(e) => set("message", e.target.value)} />
            </Grid>
          </Grid>

          {problems.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
            {/* Not "Send". It opens a mail client — saying otherwise would be
                the exact claim this page exists not to make. */}
            <Button variant="contained" color="secondary" disabled={!ready}
              href={ready ? mailtoHref(msg) : undefined}>
              Open this in my email app
            </Button>
            <Button variant="outlined" href={`mailto:${routeFor(msg.subject)}`}>
              Write to {routeFor(msg.subject)}
            </Button>
          </Stack>
        </Paper>

        <Divider sx={{ my: 4 }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 2, sm: 6 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Postal address
            </Typography>
            <Typography variant="body2" color="text.secondary" component="address" sx={{ fontStyle: "normal" }}>
              {ENTITY.name}<br />
              {ENTITY.address.map((l) => <span key={l}>{l}<br /></span>)}
              India<br />
              {entityIdentifiersLine()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Data protection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              For a request or grievance under DPDP 2023, the appointed Grievance Officer and
              their contact details are in the{" "}
              <Link component={RouterLink} to="/legal/privacy">Privacy Policy</Link>.
            </Typography>
          </Box>
        </Stack>
      <VersionLine />
      </Container>
    </Box>
  );
}
